import { boolean, ref, string, number, object, array } from 'yup';
import moment from 'moment';
import { DATE_INPUT_FORMAT } from 'src/shared/constants';

interface SchemeOptions {
  required?: boolean;
  allowEmpty?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  valid?: boolean;
  minMessage?: string;
  message?: string;
  format?: boolean;
}

export const EMAIL_REGEXP = /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
export const PROJECT_NAME_REGEXP = /^[a-zA-Z0-9Ѐ-ӿ].{0,125}$/;
const PROJECT_NAME_MESSAGE = 'Must start with a letter or number, max 126 characters.';
const URL_REGEXP =
  /(?:https?:\/\/(?:www\.|(?!www))|www\.)[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.\S{2,}/;
// http(s)://<host ending in .tld>/<...>/articles/<digits>..., checked in linear steps.
const isZendeskArticleUrl = (value: string) => {
  const match = /^https?:\/\/([a-zA-Z0-9.-]+)\/(.*)$/.exec(value);
  if (!match) return false;

  const [, host, path] = match;
  const tldStart = host.lastIndexOf('.');

  return (
    tldStart > 0 && /^[a-zA-Z]{2,}$/.test(host.slice(tldStart + 1)) && /\/articles\/\d/.test(path)
  );
};

export const emailScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .email('Invalid format')
    .required('Required')
    .matches(EMAIL_REGEXP, 'Invalid format')
    .strict();

  if (opt?.required) {
    return rule.required();
  }

  if (opt?.allowEmpty) {
    return string();
  }

  return rule;
};

export const booleanScheme = (opt?: SchemeOptions) => {
  const rule = boolean();
  if (opt?.required) return rule.required();

  return rule;
};

export const passwordScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!value : true;
    })
    .when({
      is: (value: string) => value.length > 0,
      then: () =>
        string()
          .min(opt?.min || 8, `${opt?.minMessage}`)
          .max(opt?.max || 50)
          .label('Password'),
    });
};

export const passwordConfirmScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!value : true;
    })
    .when('password', (pass: string[], schema) => {
      if (pass[0]) {
        return passwordScheme({ required: true });
      }
      return schema;
    })
    .when({
      is: (value: string) => value.length > 0,
      then: () =>
        string()
          .min(opt?.min || 8)
          .max(opt?.max || 50)
          .oneOf([ref('password')], 'Your passwords do not match.')
          .label('Password confirm'),
    });
};

export const stringScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .min(opt?.min || 0, `${opt?.minMessage}`);
};

export const projectNameScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value) => {
      return opt?.required ? !!(value ?? '').trim() : true;
    })
    .test('format', PROJECT_NAME_MESSAGE, (value) => {
      return value ? PROJECT_NAME_REGEXP.test(value) : true;
    });
};

export const numberScheme = (opt?: SchemeOptions) => {
  return number().test('required', 'Required', (value) => {
    return opt?.required ? typeof value === 'number' : true;
  });
};

export const urlScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .required('Required')
    .matches(URL_REGEXP, 'Invalid Format. Should be https://**.**')
    .strict();

  if (opt?.required) return rule.required();

  return rule;
};

export const urlZendeskArticlesScheme = (opt?: SchemeOptions) => {
  const rule = string()
    .required('Required')
    .test(
      'zendesk-article-url',
      'Enter a valid article link that includes https://**/**/articles/**',
      (value) => value == null || isZendeskArticleUrl(value),
    )
    .strict();

  if (opt?.required) return rule.required();

  return rule;
};

export const languageScheme = (opt?: SchemeOptions) => {
  const rule = object().shape({
    key: string(),
    value: string(),
  });

  if (opt?.required) return rule.required();

  return rule;
};

export const excludedLanguagesScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      key: string(),
      value: string(),
    }),
  );

  if (opt?.required) return rule.min(1, 'Excluded Languages is required');

  return rule;
};

export const websiteLinksScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      pf_page_name: string(),
      pf_page_description: string(),
      pf_page_url: string(),
    }),
  );
  if (opt?.required) return rule.min(1, 'Website Links is required');

  return rule;
};

export const arrayOfStringsScheme = (opt?: SchemeOptions) => {
  const rule = array().of(string());

  if (opt?.required) return rule.min(1, opt.message || '');

  return rule;
};

export const arrayOfCtaScheme = (opt?: SchemeOptions) => {
  const rule = array().of(
    object().shape({
      pf_cta_value: string().required('CTA is required'),
      pf_cta_condition: string().required('Condition is required'),
    }),
  );
  if (opt?.required) return rule.min(1, 'CTA is required');

  return rule;
};

export const momentScheme = (opt?: SchemeOptions) => {
  return string().test('valid', 'Invalid Date', (value) => {
    if (opt?.required) {
      // Strict parse in the MaskedDateInput format; a non-ISO string without a format makes
      // moment fall back to the inconsistent native Date() parser.
      const date = moment(value, DATE_INPUT_FORMAT, true);

      return date.isValid();
    }

    return true;
  });
};

export const stringEmailScheme = (opt?: SchemeOptions) => {
  return string()
    .test('required', 'Required', (value = '') => {
      return opt?.required ? !!value.trim() : true;
    })
    .test('format', 'Invalid Format', (value = '') => {
      if (opt?.format) {
        const arr = value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        return !arr.filter((email) => {
          return !EMAIL_REGEXP.test(email);
        }).length;
      }

      return true;
    });
};
